// ReceiptUpload.jsx - Dekont yükleme componenti (Otomatik yükleme)
import React, { useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from '../../firebase';
import { uploadReceipt } from '../../utils/newApi';

const ReceiptUpload = ({ reservationId, onUploadComplete, disabled = false }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [uploadedPath, setUploadedPath] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  // Dosya yükleme işlemi
  const uploadFile = async (file) => {
    setUploading(true);
    setError('');
    setProgress(0);

    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Kullanıcı girişi bulunamadı. Lütfen giriş yapın.');
      }

      const timestamp = Date.now();
      const storagePath = reservationId 
        ? `receipts/${reservationId}/${timestamp}_${file.name}`
        : `receipts/temp/${userId}/${timestamp}_${file.name}`;
      
      console.log('Otomatik yükleme başlıyor:', storagePath);
      
      const storageRef = ref(storage, storagePath);
      
      const metadata = {
        customMetadata: {
          uploadedBy: userId,
          uploadedAt: new Date().toISOString()
        }
      };
      
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Yükleme:', Math.round(prog) + '%');
          setProgress(Math.round(prog));
        },
        (error) => {
          console.error('Yükleme hatası:', error);
          let errorMessage = 'Yükleme sırasında hata oluştu.';
          if (error.code === 'storage/unauthorized') {
            errorMessage = 'Yükleme izniniz yok. Lütfen giriş yapın.';
          } else if (error.code === 'storage/canceled') {
            errorMessage = 'Yükleme iptal edildi.';
          }
          setError(errorMessage);
          setUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          if (reservationId) {
            const result = await uploadReceipt(reservationId, downloadURL);
            if (!result.success) {
              throw new Error('Dekont kaydı başarısız');
            }
          }

          setUploading(false);
          setProgress(100);
          setUploaded(true);
          setUploadedUrl(downloadURL);
          setUploadedPath(storagePath);
          
          if (onUploadComplete) {
            onUploadComplete(downloadURL);
          }
        }
      );

    } catch (err) {
      console.error('Yükleme hatası:', err);
      let errorMessage = 'Dekont yüklenirken hata oluştu.';
      
      // Timeout/Deadline hatası
      if (err.message?.includes('deadline-exceeded') || err.code === 'functions/deadline-exceeded') {
        errorMessage = 'İşlem zaman aşımına uğradı. Lütfen daha küçük bir dosya yükleyerek yeniden deneyin veya internet bağlantınızı kontrol edin.';
      } 
      // Ağ hatası
      else if (err.message?.includes('network') || err.code === 'storage/network-error') {
        errorMessage = 'Ağ bağlantısında sorun yaşanıyor. Lütfen internet bağlantınızı kontrol edip yeniden deneyin.';
      }
      // Boyut hatası
      else if (err.message?.includes('size')) {
        errorMessage = 'Dosya çok büyük. Lütfen 10MB\'dan küçük bir dosya seçin.';
      }
      // Custom error message
      else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setUploading(false);
    }
  };

  // Dosya seçimi - otomatik yükleme başlatır
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  // Drag & Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled || uploading) return;
    
    const file = e.dataTransfer.files[0];
    processFile(file);
  };

  // Dosya işleme ve otomatik yükleme
  const processFile = (file) => {
    if (!file) return;

    // Format kontrolü
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Sadece JPG, PNG, WEBP veya PDF formatında dosya yükleyebilirsiniz.');
      return;
    }

    // Boyut kontrolü (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Dosya boyutu 10MB\'dan küçük olmalıdır.');
      return;
    }

    setError('');
    setSelectedFile(file);

    // Preview oluştur
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview('PDF');
    }

    // Otomatik yükleme başlat
    uploadFile(file);
  };

  // Sıfırla ve sil
  const handleDelete = async () => {
    try {
      // Storage'dan sil
      if (uploadedPath) {
        const fileRef = ref(storage, uploadedPath);
        await deleteObject(fileRef);
      }
      
      // State'leri sıfırla
      setSelectedFile(null);
      setPreview(null);
      setProgress(0);
      setError('');
      setUploaded(false);
      setUploadedUrl(null);
      setUploadedPath(null);
      
      // Parent'a bildir
      if (onUploadComplete) {
        onUploadComplete(null);
      }
    } catch (err) {
      console.error('Silme hatası:', err);
      setError('Dosya silinirken hata oluştu: ' + err.message);
    }
  };

  return (
    <div className="space-y-4">
      {/* Yüklenmiş Dekont */}
      {uploaded && uploadedUrl && (
        <div className="bg-green-900/20 border-2 border-green-600 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <svg className="w-8 h-8 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-green-300">Dekont Başarıyla Yüklendi</h3>
                <p className="text-sm text-gray-400">{selectedFile?.name}</p>
              </div>
            </div>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Sil ve Yeniden Yükle
            </button>
          </div>
          
          {/* Preview */}
          {preview && preview !== 'PDF' && (
            <img 
              src={preview} 
              alt="Dekont" 
              className="w-full max-h-64 object-contain rounded-lg border border-green-600"
            />
          )}
          {preview === 'PDF' && (
            <div className="w-full p-8 bg-gray-800 rounded-lg border border-green-600 flex flex-col items-center justify-center">
              <svg className="w-16 h-16 text-green-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <p className="text-green-300 font-semibold">PDF Dosyası Yüklendi</p>
            </div>
          )}
        </div>
      )}

      {/* Dosya Seçimi - sadece yüklenmediyse ve yükleme yapılmıyorsa göster */}
      {!uploaded && !uploading && (
        <div 
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
            isDragOver 
              ? 'border-blue-400 bg-blue-900/20' 
              : 'border-gray-600 hover:border-blue-500'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
            onChange={handleFileSelect}
            disabled={disabled}
            className="hidden"
            id="receipt-upload"
          />
          <label htmlFor="receipt-upload" className={disabled ? "cursor-not-allowed" : "cursor-pointer"}>
            <svg className={`w-16 h-16 mx-auto mb-4 transition-colors ${isDragOver ? 'text-blue-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-gray-100 mb-2 font-medium">
              {isDragOver ? 'Dosyayı bırakın...' : 'Dekont dosyasını sürükleyin veya tıklayın'}
            </p>
            <p className="text-sm text-gray-400">JPG, PNG, WEBP veya PDF (Maks. 10MB)</p>
            <p className="text-xs text-blue-400 mt-2">Dosya seçildiğinde otomatik yüklenecek</p>
          </label>
        </div>
      )}

      {/* Progress Bar - Yükleme sırasında göster */}
      {uploading && (
        <div className="space-y-3">
          {preview === 'PDF' ? (
            <div className="w-full p-8 bg-gray-800 rounded-lg border border-gray-600 flex flex-col items-center justify-center opacity-50">
              <svg className="w-16 h-16 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-100 text-sm mt-2">{selectedFile?.name}</p>
            </div>
          ) : (
            <div className="relative">
              <img 
                src={preview} 
                alt="Dekont önizleme" 
                className="w-full max-h-96 object-contain rounded-lg border border-gray-600 opacity-50"
              />
            </div>
          )}
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-200">Yükleniyor...</span>
              <span className="text-blue-400 font-semibold">{progress}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      {/* Uyarı - sadece yüklenmediyse göster */}
      {!uploaded && (
        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-sm text-yellow-200">
              <strong>Önemli:</strong> Lütfen gerçek havale dekontunuzu yükleyiniz. 
              Sahte veya geçersiz dekont yüklemek ciddi yaptırımlara sebep olur.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptUpload;
